import { css } from 'styled-components';

import { SternumTheme } from '@sternum/theme';
import { TagSize, TagTheme, TagVariantTheme } from '@sternum/ui';

import { Colors } from '../constants';

// eslint-disable-next-line @typescript-eslint/no-unused-vars
export function SBOMLightTagTheme(theme: SternumTheme): Partial<TagTheme> {
  const getValueBySize = <T>(size: TagSize, obj: Record<TagSize, T>): T => {
    return obj[size];
  };

  const commonCss = css`
    white-space: nowrap;
    transition: background-color, color, border 0.2s ease-in-out;

    &:focus-visible {
      border: 1px solid ${Colors.Transparent};
    }
  `;

  const commonTheme: Partial<TagVariantTheme> = {
    focusColor: Colors.AquaMiddle,
    height: {
      normal: '24px',
      small: '22px',
    },
    fontWeight: {
      normal: '500',
      small: '500',
    },
    fontSize: {
      normal: '12px',
      small: '12px',
    },
    borderRadius: {
      normal: '4px',
      small: '4px',
    },
  };

  return {
    primary: {},
    secondary: {},

    'basic-filled': {
      ...commonTheme,
      backgroundColor: Colors.Light,
      color: Colors.Dark,
      border: `1px solid ${Colors.Light}`,

      tagCss: ({ isDisabled, isActive, isClickable }) => {
        return css`
          ${commonCss}

          ${isClickable &&
          css`
            background-color: ${Colors.GreyLightest};
            color: ${Colors.Dark};
            border: 1px solid ${Colors.GreyLightest};
          `}
          ${isClickable &&
          !isDisabled &&
          css`
            &:hover {
              background-color: ${Colors.GreyLighter};
              color: ${Colors.Dark};
              border: 1px solid ${Colors.GreyLighter};
            }
          `}
          ${isActive &&
          css`
            background-color: ${Colors.GreyLightest};
            color: ${Colors.BrandBlue};
            border: 1px solid ${Colors.BrandBlueLight};
          `}
          ${isDisabled &&
          css`
            background-color: ${Colors.Light};
            color: ${Colors.GreyDarkerMiddle};
            border: 1px solid ${Colors.Light};
          `}
        `;
      },
    },
    'basic-outlined': {
      ...commonTheme,
      backgroundColor: Colors.Transparent,
      color: Colors.Dark,
      border: `1px solid ${Colors.Grey}`,

      tagCss: ({ isDisabled, isActive, isClickable }) => {
        return css`
          ${commonCss}

          ${isClickable &&
          css`
            background-color: ${Colors.Transparent};
            color: ${Colors.Dark};
            border: 1px solid ${Colors.Grey};
          `}
          ${isClickable &&
          !isDisabled &&
          css`
            &:hover {
              background-color: ${Colors.GreyLighter};
              color: ${Colors.Dark};
              border: 1px solid ${Colors.GreyLighter};
            }
          `}
          ${isActive &&
          css`
            background-color: ${Colors.GreyLightest};
            color: ${Colors.BrandBlue};
            border: 1px solid ${Colors.BrandBlueLight};
          `}
          ${isDisabled &&
          css`
            background-color: ${Colors.Transparent};
            color: ${Colors.GreyDarkerMiddle};
            border: 1px solid ${Colors.GreyLighter};
          `}
        `;
      },
    },
    'icon-filled': {
      ...commonTheme,
      backgroundColor: Colors.Light,
      color: Colors.Dark,
      border: `1px solid ${Colors.Light}`,

      tagCss: ({ isDisabled, isActive, isClickable, size }) => {
        return css`
          ${commonCss};
          padding: 0 ${getValueBySize(size, { normal: 12, small: 8 })}px;
          height: ${getValueBySize(size, { normal: 30, small: 24 })}px;

          ${isClickable &&
          css`
            background-color: ${Colors.Light};
            color: ${Colors.Dark};
            border: 1px solid ${Colors.Light};
          `}
          ${isClickable &&
          !isDisabled &&
          css`
            &:hover {
              background-color: ${Colors.Light};
              color: ${Colors.Dark};
              border: 1px solid ${Colors.Light};
            }
          `}
          ${isActive &&
          css`
            background-color: ${Colors.Light};
            color: ${Colors.Dark};
            border: 1px solid ${Colors.Light};
          `}
          ${isDisabled &&
          css`
            background-color: ${Colors.Light};
            color: ${Colors.GreyDarkerMiddle};
            border: 1px solid ${Colors.Light};
          `}
        `;
      },
    },
    'icon-outlined': {
      ...commonTheme,
      backgroundColor: Colors.Transparent,
      color: Colors.Dark,
      border: `1px solid ${Colors.Grey}`,

      tagCss: ({ isDisabled, isActive, isClickable, size }) => {
        return css`
          ${commonCss};
          padding: 0 ${getValueBySize(size, { normal: 12, small: 8 })}px;
          height: ${getValueBySize(size, { normal: 30, small: 24 })}px;

          ${isClickable &&
          css`
            background-color: ${Colors.Transparent};
            color: ${Colors.Dark};
            border: 1px solid ${Colors.Grey};
          `}
          ${isClickable &&
          !isDisabled &&
          css`
            &:hover {
              background-color: ${Colors.Transparent};
              color: ${Colors.Dark};
              border: 1px solid ${Colors.Grey};
            }
          `}
          ${isActive &&
          css`
            background-color: ${Colors.Transparent};
            color: ${Colors.Dark};
            border: 1px solid ${Colors.Grey};
          `}
          ${isDisabled &&
          css`
            background-color: ${Colors.Transparent};
            color: ${Colors.GreyDarkerMiddle};
            border: 1px solid ${Colors.GreyLighter};
          `}
        `;
      },
    },
    'primary-green-filled': {
      ...commonTheme,
      backgroundColor: Colors.GreenDark,
      color: Colors.White,
      border: `1px solid ${Colors.GreenDark}`,

      tagCss: ({ isDisabled, isActive, isClickable }) => {
        return css`
          ${commonCss}

          ${isClickable &&
          css`
            background-color: ${Colors.GreenDark};
            color: ${Colors.White};
            border: 1px solid ${Colors.GreenDark};
          `}
          ${isClickable &&
          !isDisabled &&
          css`
            &:hover {
              background-color: ${Colors.GreenDark};
              color: ${Colors.White};
              border: 1px solid ${Colors.GreenDark};
            }
          `}
          ${isActive &&
          css`
            background-color: ${Colors.GreenDark};
            color: ${Colors.White};
            border: 1px solid ${Colors.GreenDark};
          `}
          ${isDisabled &&
          css`
            background-color: ${Colors.Light};
            color: ${Colors.GreyDarkerMiddle};
            border: 1px solid ${Colors.GreyLighter};
          `}
        `;
      },
    },
    'primary-green-outlined': {
      ...commonTheme,
      backgroundColor: Colors.Transparent,
      color: Colors.GreenDark,
      border: `1px solid ${Colors.GreenDark}`,

      tagCss: ({ isDisabled, isActive, isClickable }) => {
        return css`
          ${commonCss}

          ${isClickable &&
          css`
            background-color: ${Colors.Transparent};
            color: ${Colors.GreenDark};
            border: 1px solid ${Colors.GreenDark};
          `}
          ${isClickable &&
          !isDisabled &&
          css`
            &:hover {
              background-color: ${Colors.Transparent};
              color: ${Colors.GreenDark};
              border: 1px solid ${Colors.GreenDark};
            }
          `}
          ${isActive &&
          css`
            background-color: ${Colors.Transparent};
            color: ${Colors.GreenDark};
            border: 1px solid ${Colors.GreenDark};
          `}
          ${isDisabled &&
          css`
            background-color: ${Colors.Transparent};
            color: ${Colors.GreyDarkerMiddle};
            border: 1px solid ${Colors.GreyLighter};
          `}
        `;
      },
    },
    'primary-red-filled': {
      ...commonTheme,
      backgroundColor: Colors.RedDark,
      color: Colors.White,
      border: `1px solid ${Colors.RedDark}`,

      tagCss: ({ isDisabled, isActive, isClickable }) => {
        return css`
          ${commonCss}

          ${isClickable &&
          css`
            background-color: ${Colors.RedDark};
            color: ${Colors.White};
            border: 1px solid ${Colors.RedDark};
          `}
          ${isClickable &&
          !isDisabled &&
          css`
            &:hover {
              background-color: ${Colors.RedDark};
              color: ${Colors.White};
              border: 1px solid ${Colors.RedDark};
            }
          `}
          ${isActive &&
          css`
            background-color: ${Colors.RedDark};
            color: ${Colors.White};
            border: 1px solid ${Colors.RedDark};
          `}
          ${isDisabled &&
          css`
            background-color: ${Colors.Light};
            color: ${Colors.GreyDarkerMiddle};
            border: 1px solid ${Colors.GreyLighter};
          `}
        `;
      },
    },
    'primary-red-outlined': {
      ...commonTheme,
      backgroundColor: Colors.Transparent,
      color: Colors.RedDark,
      border: `1px solid ${Colors.RedDark}`,

      tagCss: ({ isDisabled, isActive, isClickable }) => {
        return css`
          ${commonCss}

          ${isClickable &&
          css`
            background-color: ${Colors.Transparent};
            color: ${Colors.RedDark};
            border: 1px solid ${Colors.RedDark};
          `}
          ${isClickable &&
          !isDisabled &&
          css`
            &:hover {
              background-color: ${Colors.Transparent};
              color: ${Colors.RedDark};
              border: 1px solid ${Colors.RedDark};
            }
          `}
          ${isActive &&
          css`
            background-color: ${Colors.Transparent};
            color: ${Colors.RedDark};
            border: 1px solid ${Colors.RedDark};
          `}
          ${isDisabled &&
          css`
            background-color: ${Colors.Transparent};
            color: ${Colors.GreyDarkerMiddle};
            border: 1px solid ${Colors.GreyLighter};
          `}
        `;
      },
    },
    'secondary-green-filled': {
      ...commonTheme,
      backgroundColor: Colors.GreenLightest,
      color: Colors.GreenDark,
      border: `1px solid ${Colors.GreenLightest}`,

      tagCss: ({ isDisabled, isActive, isClickable }) => {
        return css`
          ${commonCss}

          ${isClickable &&
          css`
            background-color: ${Colors.GreenLightest};
            color: ${Colors.GreenDark};
            border: 1px solid ${Colors.GreenLightest};
          `}
          ${isClickable &&
          !isDisabled &&
          css`
            &:hover {
              background-color: ${Colors.GreenLightest};
              color: ${Colors.GreenDark};
              border: 1px solid ${Colors.GreenLightest};
            }
          `}
          ${isActive &&
          css`
            background-color: ${Colors.GreenLightest};
            color: ${Colors.GreenDark};
            border: 1px solid ${Colors.GreenLightest};
          `}
          ${isDisabled &&
          css`
            background-color: ${Colors.Light};
            color: ${Colors.GreyDarkerMiddle};
            border: 1px solid ${Colors.GreyLighter};
          `}
        `;
      },
    },
    'secondary-green-outlined': {
      ...commonTheme,
      backgroundColor: Colors.Transparent,
      color: Colors.GreenDark,
      border: `1px solid ${Colors.GreenDark}`,

      tagCss: ({ isDisabled, isActive, isClickable }) => {
        return css`
          ${commonCss}

          ${isClickable &&
          css`
            background-color: ${Colors.Transparent};
            color: ${Colors.GreenDark};
            border: 1px solid ${Colors.GreenDark};
          `}
          ${isClickable &&
          !isDisabled &&
          css`
            &:hover {
              background-color: ${Colors.Transparent};
              color: ${Colors.GreenDark};
              border: 1px solid ${Colors.GreenDark};
            }
          `}
          ${isActive &&
          css`
            background-color: ${Colors.Transparent};
            color: ${Colors.GreenDark};
            border: 1px solid ${Colors.GreenDark};
          `}
          ${isDisabled &&
          css`
            background-color: ${Colors.GreenLightest};
            color: ${Colors.GreyDarkerMiddle};
            border: 1px solid ${Colors.GreyLighter};
          `}
        `;
      },
    },
    'secondary-orange-filled': {
      ...commonTheme,
      backgroundColor: Colors.OrangeLightest,
      color: Colors.OrangeDark,
      border: `1px solid ${Colors.OrangeLightest}`,

      tagCss: ({ isDisabled, isActive, isClickable }) => {
        return css`
          ${commonCss}

          ${isClickable &&
          css`
            background-color: ${Colors.OrangeLightest};
            color: ${Colors.OrangeDark};
            border: 1px solid ${Colors.OrangeLightest};
          `}
          ${isClickable &&
          !isDisabled &&
          css`
            &:hover {
              background-color: ${Colors.OrangeLightest};
              color: ${Colors.OrangeDark};
              border: 1px solid ${Colors.OrangeLightest};
            }
          `}
          ${isActive &&
          css`
            background-color: ${Colors.OrangeLightest};
            color: ${Colors.OrangeDark};
            border: 1px solid ${Colors.OrangeLightest};
          `}
          ${isDisabled &&
          css`
            background-color: ${Colors.Light};
            color: ${Colors.GreyDarkerMiddle};
            border: 1px solid ${Colors.GreyLighter};
          `}
        `;
      },
    },
    'secondary-orange-outlined': {
      ...commonTheme,
      backgroundColor: Colors.Transparent,
      color: Colors.OrangeDark,
      border: `1px solid ${Colors.OrangeDark}`,

      tagCss: ({ isDisabled, isActive, isClickable }) => {
        return css`
          ${commonCss}

          ${isClickable &&
          css`
            background-color: ${Colors.Transparent};
            color: ${Colors.OrangeDark};
            border: 1px solid ${Colors.OrangeDark};
          `}
          ${isClickable &&
          !isDisabled &&
          css`
            &:hover {
              background-color: ${Colors.Transparent};
              color: ${Colors.OrangeDark};
              border: 1px solid ${Colors.OrangeDark};
            }
          `}
          ${isActive &&
          css`
            background-color: ${Colors.Transparent};
            color: ${Colors.OrangeDark};
            border: 1px solid ${Colors.OrangeDark};
          `}
          ${isDisabled &&
          css`
            background-color: ${Colors.Transparent};
            color: ${Colors.GreyDarkerMiddle};
            border: 1px solid ${Colors.GreyLighter};
          `}
        `;
      },
    },
    'secondary-pink-filled': {
      ...commonTheme,
      backgroundColor: Colors.PinkLightest,
      color: Colors.BrandPink,
      border: `1px solid ${Colors.PinkLightest}`,

      tagCss: ({ isDisabled, isActive, isClickable }) => {
        return css`
          ${commonCss}

          ${isClickable &&
          css`
            background-color: ${Colors.PinkLightest};
            color: ${Colors.BrandPink};
            border: 1px solid ${Colors.PinkLightest};
          `}
          ${isClickable &&
          !isDisabled &&
          css`
            &:hover {
              background-color: ${Colors.PinkLightest};
              color: ${Colors.BrandPink};
              border: 1px solid ${Colors.PinkLightest};
            }
          `}
          ${isActive &&
          css`
            background-color: ${Colors.PinkLightest};
            color: ${Colors.BrandPink};
            border: 1px solid ${Colors.PinkLightest};
          `}
          ${isDisabled &&
          css`
            background-color: ${Colors.Light};
            color: ${Colors.GreyDarkerMiddle};
            border: 1px solid ${Colors.GreyLighter};
          `}
        `;
      },
    },
    'secondary-pink-outlined': {
      ...commonTheme,
      backgroundColor: Colors.Transparent,
      color: Colors.BrandPink,
      border: `1px solid ${Colors.BrandPink}`,

      tagCss: ({ isDisabled, isActive, isClickable }) => {
        return css`
          ${commonCss}

          ${isClickable &&
          css`
            background-color: ${Colors.Transparent};
            color: ${Colors.BrandPink};
            border: 1px solid ${Colors.BrandPink};
          `}
          ${isClickable &&
          !isDisabled &&
          css`
            &:hover {
              background-color: ${Colors.Transparent};
              color: ${Colors.BrandPink};
              border: 1px solid ${Colors.BrandPink};
            }
          `}
          ${isActive &&
          css`
            background-color: ${Colors.Transparent};
            color: ${Colors.BrandPink};
            border: 1px solid ${Colors.BrandPink};
          `}
          ${isDisabled &&
          css`
            background-color: ${Colors.Transparent};
            color: ${Colors.GreyDarkerMiddle};
            border: 1px solid ${Colors.GreyLighter};
          `}
        `;
      },
    },
    'secondary-blue-filled': {
      ...commonTheme,
      backgroundColor: Colors.BlueLightest,
      color: Colors.BlueDark,
      border: `1px solid ${Colors.BlueLightest}`,

      tagCss: ({ isDisabled, isActive, isClickable }) => {
        return css`
          ${commonCss}

          ${isClickable &&
          css`
            background-color: ${Colors.BlueLightest};
            color: ${Colors.BlueDark};
            border: 1px solid ${Colors.BlueLightest};
          `}
          ${isClickable &&
          !isDisabled &&
          css`
            &:hover {
              background-color: ${Colors.BlueLightest};
              color: ${Colors.BlueDark};
              border: 1px solid ${Colors.BlueLightest};
            }
          `}
          ${isActive &&
          css`
            background-color: ${Colors.BlueLightest};
            color: ${Colors.BlueDark};
            border: 1px solid ${Colors.BlueLightest};
          `}
          ${isDisabled &&
          css`
            background-color: ${Colors.Light};
            color: ${Colors.GreyDarkerMiddle};
            border: 1px solid ${Colors.GreyLighter};
          `}
        `;
      },
    },
  };
}
